import { Box, Button, Card, CardContent, CardMedia, Container, Typography } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import PageLayout from "../../shared/components/PageLayout";
import PageHero from "../../shared/components/PageHero";
import CTASection from "../../shared/components/CTASection";
import { serviceList } from "../../shared/utils/constants";
import { sharedStyles } from "../../shared/utils/sharedStyles";
import { customStyles } from "./index.styles";

const ServiceDetail = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useNavigate();

  const service = serviceList.find((s) => s.id === serviceId);
  
  // Get other services (excluding current service)
  const otherServices = serviceList.filter((s) => s.id !== serviceId);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  if (!service) {
    return (
      <PageLayout>
        <Container sx={{ minHeight: "60vh", py: 8 }}>
          <Typography variant="h4" textAlign="center">
            Service not found
          </Typography>
          <Box display="flex" justifyContent="center" mt={4}>
            <Button variant="contained" onClick={() => navigate("/")}>
              Back to Home
            </Button>
          </Box>
        </Container>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHero imageUrl={service.image} title={service.name} />

      {/* Content Section */}
      <Container sx={sharedStyles.contentContainer}>
        <Typography variant="h4" sx={sharedStyles.sectionTitle}>
          About This Service
        </Typography>
        
        {service.description.split('\n').map((paragraph, index) => (
          paragraph.trim() && (
            <Typography 
              key={index} 
              variant="body1"
              color="text.secondary"
              sx={{ ...sharedStyles.paragraph, mb: 3 }}
            >
              {paragraph}
            </Typography>
          )
        ))}

        <CTASection />
      </Container>

      {/* Other Services Section */}
      <Box sx={customStyles.otherServicesSection}>
        <Container>
          <Typography variant="h4" sx={customStyles.otherServicesTitle}>
            Explore Our Other Services
          </Typography>
          
          <Carousel
            responsive={{
              desktop: {
                breakpoint: { max: 3000, min: 1024 },
                items: 4,
                slidesToSlide: 1
              },
              tablet: {
                breakpoint: { max: 1024, min: 464 },
                items: 2,
                slidesToSlide: 1
              },
              mobile: {
                breakpoint: { max: 464, min: 0 },
                items: 1,
                slidesToSlide: 1
              }
            }}
            infinite={true}
            autoPlay={false}
            arrows={true}
            containerClass="carousel-container"
            itemClass="carousel-item-padding"
            customLeftArrow={
              <button
                style={{
                  position: "absolute",
                  left: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: customStyles.carouselNavButton.backgroundColor,
                  color: customStyles.carouselNavButton.color,
                  border: "none",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  cursor: "pointer",
                  zIndex: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                  padding: "0",
                  margin: "0"
                }}
              >
                <span style={{ display: "block", marginTop: "-2px" }}>‹</span>
              </button>
            }
            customRightArrow={
              <button
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: customStyles.carouselNavButton.backgroundColor,
                  color: customStyles.carouselNavButton.color,
                  border: "none",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  cursor: "pointer",
                  zIndex: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                  padding: "0",
                  margin: "0"
                }}
              >
                <span style={{ display: "block", marginTop: "-2px" }}>›</span>
              </button>
            }
          >
            {otherServices.map((otherService) => (
              <Box key={otherService.id} sx={{ px: 1 }}>
                <Card 
                  sx={customStyles.serviceCard}
                  onClick={() => {
                    navigate(`/service/${otherService.id}`);
                    window.scrollTo(0, 0);
                  }}
                >
                  <CardMedia
                    component="img"
                    height="150"
                    image={otherService.image}
                    alt={otherService.name}
                    sx={customStyles.serviceCardImage}
                  />
                  <CardContent>
                    <Typography 
                      variant="h6" 
                      sx={customStyles.serviceCardTitle}
                    >
                      {otherService.name}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={customStyles.serviceCardDescription}
                    >
                      {otherService.shortDescription}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Carousel>
        </Container>
      </Box>
    </PageLayout>
  );
};

export default ServiceDetail;
